import type { FeatureArea } from "../../lib/feedbackFeatureAreas";
import { FEATURE_AREA_LABELS } from "../../lib/feedbackFeatureAreas";
import { getFeatureAreaOverride } from "../../utils/featureAreaOverride";

// Human-readable label for "what the user was looking at" — resolved from the
// current route so an admin triaging feedback doesn't have to decode a raw URL.
// Matched by path prefix, most-specific first. Falls back to document.title,
// then the raw pathname, so every submission always gets *some* label.

const ROUTE_LABELS: Array<{ prefix: string; label: string }> = [
  { prefix: "/activities/run", label: "Running an activity" },
  { prefix: "/review", label: "Reviewing a process" },
  { prefix: "/pwf", label: "Viewing a PWF" },
  { prefix: "/chats", label: "In a chat" },
  { prefix: "/programs", label: "Viewing programs" },
  { prefix: "/spaces", label: "Viewing a space" },
  { prefix: "/mentoring", label: "Mentoring view" },
  { prefix: "/marketplace", label: "Browsing the marketplace" },
  { prefix: "/portal", label: "Home portal" },
  { prefix: "/admin", label: "Admin area" },
  { prefix: "/contact", label: "Contact page" },
];

export const resolveCurrentContextLabel = (pathname: string): string => {
  const match = ROUTE_LABELS.find((entry) => pathname.startsWith(entry.prefix));
  if (match) return match.label;

  if (typeof document !== "undefined" && document.title) {
    return document.title;
  }

  return pathname || "Unknown page";
};

// Route-prefix → feature area, most-specific first. Routes with a real
// view-mode split (localStorage-backed) are resolved by reading that key
// directly below rather than listed here.
const ROUTE_FEATURE_AREAS: Array<{ prefix: string; area: FeatureArea }> = [
  { prefix: "/design", area: "design_canvas" },
  { prefix: "/activities/run", area: "activities_run" },
  { prefix: "/review", area: "review" },
  { prefix: "/meetings/builder", area: "meetings_builder" },
  { prefix: "/meetings", area: "meetings_room" },
  { prefix: "/chats", area: "chats_dm" },
  { prefix: "/programs", area: "programs" },
  { prefix: "/spaces", area: "spaces" },
  { prefix: "/mentoring", area: "mentoring" },
  { prefix: "/marketplace", area: "marketplace" },
  { prefix: "/insights", area: "insights" },
  { prefix: "/account", area: "account" },
  { prefix: "/admin", area: "admin" },
  { prefix: "/contact", area: "contact" },
  { prefix: "/examples", area: "marketing_pages" },
  { prefix: "/plans", area: "marketing_pages" },
  { prefix: "/science", area: "marketing_pages" },
  { prefix: "/capture", area: "marketing_pages" },
  { prefix: "/login", area: "marketing_pages" },
  { prefix: "/register", area: "marketing_pages" },
];

// Resolves which of the fixed feedback feature areas (see
// lib/feedbackFeatureAreas.ts) a submission belongs to, for auto-assignment.
// Order of precedence:
//   1. An explicit override set by the owning component (Design's
//      canvas/sketchbook/preview panels, Chats' DM/AI-builder split) — these
//      live in component useState, not anything a route-only function can
//      see, so the component itself is the source of truth when set.
//   2. Routes with a real localStorage-backed view-mode split (Home,
//      PWF) — read the same global keys those pages already write.
//   3. Plain route-prefix matching.
//   4. "uncategorized" fallback — the same bucket a missing/unmapped area
//      maps to server-side (falls to the designated fallback assignee).
export const resolveFeatureArea = (pathname: string): FeatureArea => {
  const override = getFeatureAreaOverride();
  if (override) return override;

  if (pathname.startsWith("/pwf")) {
    const mode = typeof localStorage !== "undefined" ? localStorage.getItem("pwf_view_mode") : null;
    return mode === "studio" ? "pwf_studio" : "pwf_classic";
  }

  if (pathname.startsWith("/home")) {
    const view = typeof localStorage !== "undefined" ? localStorage.getItem("home_view") : null;
    return view === "personal" ? "home_personal" : "home_studio";
  }

  if (pathname.startsWith("/portal")) {
    const tab = typeof localStorage !== "undefined" ? localStorage.getItem("portal_active_tab") : null;
    if (tab === "my_workspace") return "portal_workspace";
    if (tab === "my_groups") return "portal_groups";
    return "portal_activities";
  }

  const match = ROUTE_FEATURE_AREAS.find((entry) => pathname.startsWith(entry.prefix));
  if (match) return match.area;

  return "uncategorized";
};

export { FEATURE_AREA_LABELS };
